/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { useEffect, useState } from 'preact/compat';
import { events } from '@adobe-commerce/event-bus';
import { ProductModel } from '@/pdp/data/models';
import { ValuesModel } from '@/pdp/data/models/values-model';

export interface UseProductDataOptions {
  scope?: string;
  initialData?: ProductModel | null;
  initialValues?: Partial<ValuesModel>;
}

export interface UseProductDataResult {
  data: ProductModel | null;
  values: ValuesModel;
}

/**
 * Custom hook for subscribing to PDP data and values events.
 * Provides a reusable way to access product data and configuration values
 * across container components.
 */
export function useProductData({
  scope,
  initialData = null,
  initialValues = {},
}: UseProductDataOptions = {}): UseProductDataResult {
  const [data, setData] = useState<ProductModel | null>(initialData);
  const [values, setValues] = useState<ValuesModel>({
    sku: '',
    quantity: 1,
    ...initialValues,
  });

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });
    const valuesEvent = events.on('pdp/values', setValues, {
      eager: true,
      scope,
    });

    return () => {
      dataEvent?.off();
      valuesEvent?.off();
    };
  }, [scope]);

  return { data, values };
}

export interface AttributeResult<T = any> {
  id: string;
  label: string;
  value: T;
}

/**
 * Utility function to find and parse all attributes by their ID from product data.
 * Returns an array of all matching attributes.
 * Handles JSON parsing of attribute values and provides type safety.
 * If JSON parsing fails, returns the attribute with the raw value or a default empty structure.
 */
export function getAttributesById<T = any>(
  data: ProductModel | null,
  attributeId: string
): AttributeResult<T>[] {
  if (!data?.attributes) return [];

  return data.attributes
    .filter(({ id }: { id: string }) => id === attributeId)
    .map((attr) => {
      try {
        const value =
          typeof attr.value === 'string' ? JSON.parse(attr.value) : attr.value;
        return { id: attr.id, label: attr.label, value };
      } catch {
        console.warn(`Invalid ${attributeId} data format`);
        // Return with raw value instead of filtering out completely
        return { id: attr.id, label: attr.label, value: attr.value as T };
      }
    });
}
